import { useMemo } from 'react';

import type { ObjectiveWindow } from '@/types/objectives';

import { useGetUserById } from '@/api/directory';
import { useGetCompanyPillarSummaries, useGetObjectivesWithDrafts } from '@/api/objectives';
import { usePeriodOptions, usePeriodParam } from '@/hooks/objectives/usePeriodOptions';
import { useViewedEmployee } from '@/pages/EmployeePortal/hooks/useViewedEmployee';
import { summarizeObjectives } from '@/utils/objectivesSummary';
import { isPublishedObjective } from '@/utils/objectiveStatus';

/**
 * What the portal's Objectives tab reads, all through one period lens kept in `?period=`: the
 * objectives of whoever's portal is open and their headline figures, and the strategic pillars
 * those objectives feed.
 *
 * The list also carries that employee's drafts the reader sees: a draft is read within the same reach
 * as a published objective, and its creator always finds it again to finish it. A draft has no
 * progress yet, so the headline figures and the pillars marked as fed count the published objectives
 * only.
 *
 * The objectives are asked for that employee by id, never "everything the reader may see": a
 * manager reading their own portal must not find their team's objectives in it, and reading an
 * employee's must find that employee's alone. The pillar totals, on the contrary, are the whole
 * company's — the section says how the organization is progressing — so every employee reads them,
 * whatever their objectives permissions.
 */
export const useEmployeePortalObjectives = () => {
    const { viewedEmployeeId } = useViewedEmployee();
    const { data: signedInUser, isError: isSignedInUserInError } = useGetUserById({ userId: 'me' });
    // Only the reader's own portal has to resolve who they are; another employee's portal is
    // already named by the address, so a directory that would not answer cannot stop it.
    const employeeId = viewedEmployeeId ?? signedInUser?.id;
    const isEmployeeUnknown = !viewedEmployeeId && isSignedInUserInError;
    const {
        options: periodOptions,
        defaultPeriodId,
        isLoading: isLoadingPeriods,
        isError: arePeriodsInError,
    } = usePeriodOptions();
    // The lens itself is shown by `ObjectivePeriodFilter`, mounted beside the portal's tabs; both
    // read it from the address, so there is nothing to pass between them.
    const { periodId, window: periodWindow } = usePeriodParam(periodOptions, defaultPeriodId, {
        allowCustomDates: true,
    });
    // Asked before the periods land, a window would span every period and then switch under the
    // reader once the default one is known.
    const arePeriodsKnown = !isLoadingPeriods && !arePeriodsInError;

    const objectivesWindow = useMemo<ObjectiveWindow>(
        () => ({ ...periodWindow, employeeId }),
        [periodWindow, employeeId],
    );
    const {
        data: objectives = [],
        isError: areObjectivesInError,
        isSuccess: areObjectivesKnown,
    } = useGetObjectivesWithDrafts(objectivesWindow, arePeriodsKnown && !!employeeId);
    const publishedObjectives = useMemo(
        () => objectives.filter(isPublishedObjective),
        [objectives],
    );
    const summary = useMemo(() => summarizeObjectives(publishedObjectives), [publishedObjectives]);

    const pillarsQuery = useGetCompanyPillarSummaries(periodWindow, arePeriodsKnown);

    const contributingPillarIds = useMemo(
        () => new Set(publishedObjectives.flatMap((objective) => objective.pillarIds)),
        [publishedObjectives],
    );

    const areObjectivesUnavailable = isEmployeeUnknown || arePeriodsInError || areObjectivesInError;

    return {
        periodId,
        objectives,
        summary,
        // `summarizeObjectives([])` answers zero for everything, and the list is empty before the
        // request lands: nothing is shown as a figure until the answer is in.
        isSummaryKnown: areObjectivesKnown,
        // A request held back for the reader or the period is on its way, not an empty answer.
        isLoadingObjectives: !areObjectivesUnavailable && !areObjectivesKnown,
        areObjectivesUnavailable,
        pillars: {
            summaries: pillarsQuery.data ?? [],
            isKnown: pillarsQuery.isSuccess,
            isError: arePeriodsInError || pillarsQuery.isError,
        },
        contributingPillarIds,
    };
};
